import type {Anchor, CellAddress, ItemId} from '../types'

/**
 * Where in a cell a drop points, worked out from the cards that are drawn.
 *
 * No React and no DOM, so every rule here is exercised without a live `DndContext` and
 * without a layout, the way `drag/resolve-drop.ts` keeps its own answer pure.
 *
 * **The cell is still the only drop target**, and nothing here changes that. A cell inside
 * the board's windows is always rendered, so a drag always has something present to aim
 * at; a target attached to a card would not hold that, because a cell windows its cards
 * and most of them are not in the page. So the cell answers *which* cell, exactly as it
 * did, and this answers *where in it* from the run of cards a reader can actually see.
 */

/** One card that is drawn, and where it sits: in the cell's loaded ids, and on screen. */
export type DrawnCard = {
  /** Index among the cell's loaded ids. Not the position in the drawn run. */
  index: number
  /** The card's block-start edge, in client coordinates. */
  start: number
  /** Its block-end edge, in the same coordinates. */
  end: number
}

/** Where a drop points: which cell, and where among that cell's cards. */
export type BoardDropPlacement = CellAddress & {
  /**
   * Where the cards land, or absent when the cell holds nothing loaded to anchor against
   * and has more to come. See {@link cellDropAnchor}.
   */
  anchor?: Anchor
}

/**
 * Where a pointer at `pointerY` would insert, as an index among the cell's loaded ids, or
 * `null` when the cell draws no card to measure against.
 *
 * Each drawn card is split at its midpoint: above it the card is pushed down, below it the
 * card is passed. That is what makes the whole cell answer something -- every point in the
 * band belongs to exactly one card -- where a rule written on the gaps between cards would
 * leave the cards themselves meaning nothing.
 *
 * **Measured against the drawn run rather than the loaded one.** `cards` holds what is in
 * the page, which for a windowed cell is the part a reader can see plus a little either
 * side, and the reserved boxes standing in for the rest are off screen while the pointer is
 * over the cell. So a pointer above the first drawn card lands before it and one below the
 * last lands after it, which is what a reader aiming at either end of what they can see is
 * asking for.
 */
export function insertionIndexAt(pointerY: number, cards: readonly DrawnCard[]): number | null {
  let last: DrawnCard | undefined

  for (const card of cards) {
    if (pointerY < (card.start + card.end) / 2) return card.index
    last = card
  }

  return last === undefined ? null : last.index + 1
}

/**
 * The anchor for an insertion index, in the words `Anchor` states a position in.
 *
 * Named against a neighbouring card wherever there is one, rather than as an end of the
 * cell. A cell pages, so its loaded cards are a prefix of what it holds: `{at: 'end'}` on a
 * cell with another page waiting names a place below cards nobody has fetched, which is not
 * where the reader dropped. `{after: <the last loaded card>}` is the same place a reader
 * aimed at and stays true whatever arrives later.
 *
 * Absent for a cell that has loaded nothing and says it has more, which is the one case
 * with nothing honest to say: there is no card to anchor against, and either end of it
 * would be a guess about cards that have not arrived. A cell that has loaded nothing and
 * has nothing more really is empty, so `{at: 'start'}` there is a fact.
 */
export function cellDropAnchor(
  itemIds: readonly ItemId[],
  index: number,
  {hasMoreItems}: {hasMoreItems: boolean},
): Anchor | undefined {
  const first = itemIds[0]
  const last = itemIds[itemIds.length - 1]
  if (first === undefined || last === undefined) return hasMoreItems ? undefined : {at: 'start'}

  if (index <= 0) return {before: first}

  const following = itemIds[index]

  return following === undefined ? {after: last} : {before: following}
}

/**
 * Whether an insertion inside the cell a card came from would leave it where it already is.
 *
 * Both sides of a card are the place it already occupies: dropping it just above itself and
 * just below itself are the two halves of its own row, and neither is a move. Testing the
 * anchor instead would catch only one of them, because the anchor above a card names the
 * card before it rather than the card itself.
 *
 * Only ever asked of the cell the drag started in. Every index in any other cell is a real
 * destination, including the one the card would occupy there.
 */
export function isRedundantInsertion(sourceIndex: number, index: number): boolean {
  return index === sourceIndex || index === sourceIndex + 1
}
